import { Mark } from '~/deck2/ui/Mark';
import { Icon } from '~/design-system/elements/Icon/Icon';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { OpeningNotice, isNotOpenYet } from '~/design-system/components/Auth/OpeningNotice/OpeningNotice';
import { cn } from '~/lib/utils';
import brand from '../brand.module.css';
import styles from './SignInUI.module.css';

/**
 * Sign in, in the landing's colours and type (`../brand.module.css`), so it reads as the same Deck as
 * usedeck.ai. An email with no account before Deck opens gets the opening notice, not an error. The
 * props are unchanged.
 */

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M14.4 8.18182C14.4 7.61818 14.3545 7.27273 14.2545 6.90909H8V9.23636H11.6182C11.5273 9.85455 11.1273 10.7273 10.2182 11.3091L10.2073 11.3782L12.2327 12.9527L12.3818 12.9673C13.6909 11.7818 14.4 10.1455 14.4 8.18182Z" fill="#4285F4" />
    <path d="M8 14.5455C9.74545 14.5455 11.2182 13.9636 12.3818 12.9673L10.2182 11.3091C9.65455 11.6909 8.90909 11.9455 8 11.9455C6.29091 11.9455 4.83636 10.76 4.30909 9.16364L4.24364 9.16909L2.14182 10.8073L2.12 10.8691C3.27273 13.1636 5.48182 14.5455 8 14.5455Z" fill="#34A853" />
    <path d="M4.30909 9.16364C4.13636 8.75455 4.03636 8.30909 4.03636 7.84545C4.03636 7.38182 4.13636 6.93636 4.3 6.52727L4.29545 6.45273L2.16727 4.78909L2.12 4.81818C1.65455 5.74545 1.38182 6.76364 1.38182 7.84545C1.38182 8.92727 1.65455 9.94545 2.12 10.8691L4.30909 9.16364Z" fill="#FBBC05" />
    <path d="M8 3.74545C9.14545 3.74545 9.92727 4.21818 10.3727 4.62727L12.2636 2.78182C11.2091 1.7 9.74545 1.14545 8 1.14545C5.48182 1.14545 3.27273 2.52727 2.12 4.81818L4.3 6.52727C4.83636 4.93091 6.29091 3.74545 8 3.74545Z" fill="#EB4335" />
  </svg>
);

export type SignInView = 'form' | 'magic-link-sent' | 'continue-in-browser';

/**
 * The sign-in ground while the auth context resolves, or while a session that appeared in another
 * tab redirects this one. Agent dots rather than the shared spinner, which paints a white veil.
 */
export const SignInWaiting = () => (
  <div className={brand.brand}>
    <div className={styles.waiting} role="status" aria-label="Loading">
      <span className={styles.dot} />
      <span className={styles.dot} />
      <span className={styles.dot} />
    </div>
  </div>
);

export interface SignInUIProps {
  view?: SignInView;
  email?: string;
  onEmailChange?: (email: string) => void;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onReset?: () => void;
  onGoogleSignIn?: () => void;
  /** `continue-in-browser` only: opens the browser's sign-in page again, for a tab closed by mistake. */
  onOpenBrowserAgain?: () => void;
  error?: string | null;
  showCooldownMessage?: boolean;
  cooldownSeconds?: number;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  FormComponent?: React.ElementType;
  showGoogleSignIn?: boolean;
  /** The bot check, placed between the field and the button. */
  captcha?: React.ReactNode;
  /** False while the bot check has no answer yet: the button waits, the field stays usable. */
  isSubmitReady?: boolean;
  /** A problem that is not the email's: shown like an error, without marking the field. */
  notice?: string | null;
}

export const SignInUI = ({
  view = 'form',
  email = '',
  onEmailChange,
  onSubmit,
  onReset,
  onGoogleSignIn,
  onOpenBrowserAgain,
  error,
  showCooldownMessage = false,
  cooldownSeconds = 0,
  isSubmitting = false,
  isDisabled = false,
  FormComponent,
  showGoogleSignIn = false,
  captcha,
  isSubmitReady = true,
  notice,
}: SignInUIProps) => {
  const FormTag = FormComponent ?? 'form';
  const submitLabel = isSubmitting
    ? 'Sending...'
    : isDisabled && cooldownSeconds > 0
      ? `Wait ${cooldownSeconds}s`
      : 'Send magic link';

  // Deck for Mac hands Google to the default browser, and the app comes back on its own when it is done.
  if (view === 'continue-in-browser') {
    return (
      <div className={brand.brand}>
        <div className={styles.card}>
          <div className={styles.successHeader}>
            <span className={styles.successIcon}>
              <DeckIcon name="global" size={24} />
            </span>
            <div className={styles.textContainer}>
              <h1 className={styles.successTitle}>Continue in your browser</h1>
              <div className={styles.successSentence}>
                <p className={styles.successDescription}>Pick your Google account there.</p>
                <p className={styles.successDescription}>Deck opens again when you are signed in.</p>
              </div>
            </div>
          </div>
          {onOpenBrowserAgain && (
            <button type="button" onClick={onOpenBrowserAgain} className={styles.resetLink}>
              Open the browser again
            </button>
          )}
          <button type="button" onClick={onReset} className={styles.resetLink}>
            Sign in another way
          </button>
        </div>
      </div>
    );
  }

  if (view === 'magic-link-sent') {
    return (
      <div className={brand.brand}>
        <div className={styles.card}>
          <div className={styles.successHeader}>
            <span className={styles.successIcon}>
              <DeckIcon name="mail_check" size={24} />
            </span>
            <div className={styles.textContainer}>
              <h1 className={styles.successTitle}>Magic link sent</h1>
              <div className={styles.successSentence}>
                <p className={styles.successDescription}>We sent a sign in link to</p>
                <p className={styles.email}>{email}</p>
              </div>
            </div>
          </div>
          <button type="button" onClick={onReset} className={styles.resetLink}>
            Sign in with another email address
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={brand.brand}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Mark size={34} className={styles.mark} />
          <div className={styles.textContainer}>
            <h1 className={styles.title}>Welcome to Deck</h1>
            <p className={styles.description}>Enter your email to receive a magic link</p>
          </div>
        </div>

        <FormTag method="post" className={styles.form} onSubmit={onSubmit}>
          <div className={styles.inputGroup}>
            <Icon name="at-line" size={16} className={styles.inputIcon} />
            <input
              id="email"
              name="email"
              type="email"
              required
              className={cn(styles.input, error && !isNotOpenYet(error) && styles.inputError)}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => onEmailChange?.(e.target.value)}
              disabled={isDisabled}
            />
          </div>

          {!error && notice && <p className={styles.notice}>{notice}</p>}

          {error && isNotOpenYet(error) ? <OpeningNotice /> : null}

          {error && !isNotOpenYet(error) && (
            <p className={styles.notice} role="alert">
              {error}
              {showCooldownMessage && cooldownSeconds > 0 && (
                <span className={styles.errorCooldown}> Please wait {cooldownSeconds} seconds before trying again.</span>
              )}
            </p>
          )}

          {captcha}

          <button
            type="submit"
            className={cn(brand.button, brand.ink, styles.button)}
            disabled={isDisabled || !isSubmitReady}
          >
            {submitLabel}
          </button>
        </FormTag>

        {showGoogleSignIn && (
          <>
            <div className={styles.divider}>
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>Or</span>
              <div className={styles.dividerLine} />
            </div>

            <button type="button" onClick={onGoogleSignIn} className={cn(brand.button, brand.white, styles.button)}>
              <GoogleIcon />
              Continue with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
};
